/* eslint-disable lit-a11y/anchor-is-valid */
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html } from 'lit';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { setActiveTab, setAllTabs } from './store/tabbed-search.store';
import { StoreState } from '../../../store/store.interface';
import '../../search-v2/search-v2-results';
import { bs5Connect } from '../../../common/bs5Utils/bs5.mixin';
import '../../pagination/in-search/pagination';
import '../../search-v2/search-v2-pagination';
import { booleanConverter } from '../../../common/converters';
import { DEFAULT_SORT_CONFIG } from '../../search/search-results-sorting';
import { setSelectedCatalog } from '../../search/store/search.store';
import { getSearchRequestFromUrl, performSearch } from '../../search/thunks/search.thunk';
import { clearSuggestions } from '../../search/store/suggestion.store';
import { isLoading, searchParam, selectSelectedCatalog } from '../../search/store/search.selector';
import {
  checkScrollState,
  debounce,
  generateLoadingTemplate,
  getBusinessUnit,
  updateUrlParams,
} from '../utils/tabbed-search.utils';
import { TAB_NAMES, BUSINESS_UNITS } from '../../tabbed-search/constants';
import emit from '../../../common/events';
import { getUserDataForUserAnalytics } from '../../user-profile/user-profile.selectors';
import { getSearchPageAnalytics } from '../../../common/utils/analytics-util';
import { isSearchPage } from '../../../common/utils/util';

/**
 * Tabbed Search Result Component
 *
 * This component displays a tabbed interface for search results,
 * allowing users to switch between different categories of search results.
 * It includes scroll buttons for small screens and manages tab state.
 */
@customElement('mhecomm-tabbed-search-result')
export class TabbedSearchResultView extends connect(store)(bs5Connect(BaseComponent)) {
  // Tab properties customizable by users of this component
  @property()
  productTabName = 'Product';

  @property()
  supportTabName = 'Support';

  @property()
  articlesTabName = 'Articles';

  @property()
  productTabIcon = 'book';

  @property()
  supportTabIcon = 'question-circle';

  @property()
  articlesTabIcon = 'lightbulb';

  // Controls auto loading behavior
  @property({ converter: booleanConverter })
  autoLoad = false;

  // Search result display properties
  @property()
  variation = 'small-card';

  @property()
  view = '';

  @property({ type: Number })
  maxPagesShown = 3;

  @property()
  message = 'Showing %first%-%last% of %total% Results Matching "%term%"';

  // Internal state properties
  @state()
  private activeTabKey = TAB_NAMES.PRODUCT;

  @state()
  private isLoading = false;

  @state()
  private isSearchLoading = false;

  @state()
  private isScrollable = false;

  @state()
  private canScrollLeft = false;

  @state()
  private canScrollRight = false;

  // Track current business unit
  private bu = 'products';
  private debouncedCheckScrollability: () => void;
  private tabBusinessUnits!: Record<string, string>;
  // Store the event handler as a class property
  private skipToResultsTabHandler: () => void;

  private tabValueMap!: Record<string, string>;

  constructor() {
    super();

    // Initialize business unit mapping
    this.setTabValueMapping();

    // Initialize the event handler
    this.skipToResultsTabHandler = () => {
      this.shadowRoot?.getElementById('collapseOne-tab')?.focus();
    };

    // Initialize the debounced function
    this.debouncedCheckScrollability = debounce(() => this.checkScrollability(), 100);

    // Initialize business unit mapping
    this.setTabBusinessUnits();
  }

  private setTabValueMapping() {
    this.tabValueMap = {
      [TAB_NAMES.PRODUCT]: this.productTabName || TAB_NAMES.PRODUCT,
      [TAB_NAMES.SUPPORT]: this.supportTabName || TAB_NAMES.SUPPORT,
      [TAB_NAMES.ARTICLES]: this.articlesTabName || TAB_NAMES.ARTICLES,
    };
  }

  /**
   * Lifecycle: Component connected
   */
  connectedCallback() {
    super.connectedCallback();

    // Initialize business unit mapping
    this.setTabValueMapping();

    // Set the allTabs in the Redux store
    store.dispatch(setAllTabs(this.tabValueMap));

    // Check URL for tab parameter
    this.initializeFromUrl();

    // Add resize event listener with debounce
    window.addEventListener('resize', this.debouncedCheckScrollability);

    // Add the skip-to-results-tab event listener
    window.addEventListener('skip-to-results-tab', this.skipToResultsTabHandler);

    // Initial check after a short delay to ensure DOM is ready
    setTimeout(() => this.checkScrollability(), 100);
  }

  /**
   * Initialize the component state from URL parameters
   */
  private initializeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    let tabParam = urlParams.get('tab') || '';
    const bu = urlParams.get('bu');
    this.activeTabKey =
      tabParam && [BUSINESS_UNITS.CONTENT, BUSINESS_UNITS.ARTICLES].includes(bu ?? '') ? '' : TAB_NAMES.PRODUCT;

    if (!tabParam && bu && [BUSINESS_UNITS.UNIFIED, BUSINESS_UNITS.ALL].includes(bu)) {
      const url = new URL(window.location.href);
      url.searchParams.set('bu', BUSINESS_UNITS.PRODUCTS);
      history.pushState({}, '', url.toString());
      tabParam = TAB_NAMES.PRODUCT;
    }

    if (!tabParam && [BUSINESS_UNITS.CONTENT, BUSINESS_UNITS.ARTICLES].includes(bu ?? '')) {
      tabParam = TAB_NAMES.ARTICLES;
    }

    // If tab parameter exists and corresponds to a valid tab, select it
    if (tabParam) {
      const validTabs = [TAB_NAMES.PRODUCT, TAB_NAMES.SUPPORT, TAB_NAMES.ARTICLES];
      if (validTabs.includes(tabParam)) {
        this.selectTab(tabParam, false); // Pass false to indicate it's not a user click
        return;
      }
    }

    // Otherwise, select the default tab
    this.selectTab(this.activeTabKey, false); // Pass false to indicate it's not a user click
  }

  /**
   * Lifecycle: Component disconnected - cleanup
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    // Remove resize event listener
    window.removeEventListener('resize', this.debouncedCheckScrollability);

    // Remove the skip-to-results-tab event listener
    window.removeEventListener('skip-to-results-tab', this.skipToResultsTabHandler);
  }

  /**
   * Check if tabs are scrollable and update button states
   */
  private checkScrollability() {
    const navTabs = this.renderRoot.querySelector<HTMLElement>('.nav-tabs');
    const tabsContainer = this.renderRoot.querySelector<HTMLElement>('.tabs-container');

    if (!navTabs || !tabsContainer) return;

    // Use utility function to check scroll state
    const scrollState = checkScrollState(navTabs);

    // Update state if any values changed
    if (
      this.isScrollable !== scrollState.isScrollable ||
      this.canScrollLeft !== scrollState.canScrollLeft ||
      this.canScrollRight !== scrollState.canScrollRight
    ) {
      this.isScrollable = scrollState.isScrollable;
      this.canScrollLeft = scrollState.canScrollLeft;
      this.canScrollRight = scrollState.canScrollRight;

      // Update container class
      if (scrollState.isScrollable) {
        tabsContainer.classList.add('has-scroll-buttons');
      } else {
        tabsContainer.classList.remove('has-scroll-buttons');
      }

      this.requestUpdate();
    }
  }

  /**
   * Handle scroll to left button click
   */
  private scrollToLeft(): void {
    const navTabs = this.renderRoot.querySelector<HTMLElement>('.nav-tabs');
    if (!navTabs) return;

    navTabs.scrollBy({
      left: -100,
      behavior: 'smooth',
    });

    // Update button state after scrolling
    setTimeout(() => this.checkScrollability(), 300);
  }

  /**
   * Handle scroll to right button click
   */
  private scrollToRight(): void {
    const navTabs = this.renderRoot.querySelector<HTMLElement>('.nav-tabs');
    if (!navTabs) return;

    navTabs.scrollBy({
      left: 100,
      behavior: 'smooth',
    });

    // Update button state after scrolling
    setTimeout(() => this.checkScrollability(), 300);
  }

  // Lifecycle: After component update
  updated(changedProperties: Map<string, any>) {
    super.updated?.(changedProperties);

    if (changedProperties.has('activeTabKey')) {
      setTimeout(() => this.checkScrollability(), 0);

      // Update the business unit mapping when tab properties change
      if (
        changedProperties.has('productTabName') ||
        changedProperties.has('supportTabName') ||
        changedProperties.has('articlesTabName')
      ) {
        this.setTabBusinessUnits();
      }
    }
  }

  private setTabBusinessUnits() {
    this.tabBusinessUnits = {
      [TAB_NAMES.PRODUCT]: BUSINESS_UNITS.PRODUCTS,
      [TAB_NAMES.SUPPORT]: BUSINESS_UNITS.SUPPORT,
      [TAB_NAMES.ARTICLES]: BUSINESS_UNITS.ARTICLES,
    };
  }

  /**
   * Redux state change handler
   */
  stateChanged(state: StoreState) {
    const newTabKey = state.tabbedSearchActiveTabState.activeTabKey;

    // Check if search is loading
    const searchLoading = isLoading(state);

    // Update loading state only if it changes
    if (this.isSearchLoading !== searchLoading) {
      this.isSearchLoading = searchLoading;

      // If search is no longer loading, turn off our loading indicator
      if (!searchLoading) {
        setTimeout(() => {
          this.isLoading = false;
        }, 300); // Small delay to ensure results are rendered
      }
    }

    if (this.activeTabKey !== newTabKey) {
      this.activeTabKey = newTabKey;
    }
  }

  /**
   * Handle tab selection
   * On search pages (/search.html), SearchV3 is the coordinator for all search actions.
   * TabbedSearchResult only updates tab state and lets SearchV3 handle the search dispatch
   * to prevent duplicate searches and events.
   */
  async selectTab(tab: string, isUserClick = true) {
    if (this.activeTabKey === tab) {
      return; // Don't do anything if the same tab is clicked
    }

    // Set loading state
    this.isLoading = true;

    // Determine if search needs to be performed based on tab change
    // On search pages, SearchV3 handles the search, so skip performSearch here
    let performSearchNeeded = !isSearchPage();

    if (tab === TAB_NAMES.SUPPORT) {
      performSearchNeeded = false;
    }

    // Update the business unit (bu) based on the selected tab.
    // On initial load (isUserClick=false), preserve the URL's `bu` if it names a specific
    // catalog (he, seg, etc.) — otherwise the topbar-driven search that redirected here
    // with `?bu=he` gets silently downgraded to the tab default (`products`), which
    // makes the API request use `catalog: 'PRODUCTS'` instead of `catalog: 'HE'`.
    // User clicks (isUserClick=true) still reset to the tab default so switching tabs
    // resets scope as before.
    const tabDefaultBu = getBusinessUnit(tab, this.tabBusinessUnits);
    if (!isUserClick) {
      const urlBu = new URLSearchParams(window.location.search).get('bu');
      this.bu = urlBu ? urlBu : tabDefaultBu;
    } else {
      this.bu = tabDefaultBu;
    }

    if (isUserClick) {
      const state = store.getState() as StoreState;
      const searchParams = getSearchRequestFromUrl();
      const searchString: string = searchParams?.searchQuery || searchParam(state)!;
      const lastActiveTab = state.tabbedSearchActiveTabState.activeTabKey;

      emit({
        element: this,
        name: 'click',
        detail: {
          id: this.id,
          url: window.location.href,
          user: getUserDataForUserAnalytics(state),
          eventName: 'search-result-tab',
          comp: 'mhecomm-tabbed-search-result',
          area: 'main',
          subarea: 'body',
          searchTerm: searchString,
          tabLabel: this.tabValueMap![tab],
          tabName: tab,
          lastTabLabel: this.tabValueMap![lastActiveTab],
          lastTabName: lastActiveTab,
        },
      });
    }

    // Update the catalog BEFORE the active tab. `setActiveTab` synchronously drives the
    // SearchV3 coordinator's tab-switch dispatch, which reads `selectedCatalog` — if the
    // catalog were still the previous tab's marker it would resolve to 'UNIFIED'.
    await store.dispatch(setSelectedCatalog(this.bu.toUpperCase()));

    // Dispatch the selected tab to the store with key, value
    store.dispatch(
      setActiveTab({
        tabKey: tab,
        tabValue: this.tabValueMap![tab],
      })
    );

    // Update the URL parameters to reflect the selected tab and default settings.
    // Skip URL rewriting on the initial (non-user) load — otherwise we overwrite the
    // user's `bu=he` with the tab default and every downstream component that reads
    // the URL (e.g. search-catalog-selector) picks up the wrong catalog.
    if (isUserClick) {
      updateUrlParams(
        new Map([
          ['page', '1'], // Reset to the first page
          ['sortby', DEFAULT_SORT_CONFIG[0].value], // Use default sorting
          ['tab', tab], // Set the selected tab
          ['bu', this.bu], // Set the business unit
        ])
      );
    }

    // Perform search only if necessary
    if (performSearchNeeded) {
      this.performSearch();
    } else {
      // If no search needed, manually stop loading after a short delay
      setTimeout(() => {
        this.isLoading = false;
      }, 500);
    }
  }

  /**
   * Perform search with updated parameters
   */
  private async performSearch() {
    // If search is successful but no results, perform a new search with no filters
    const state = store.getState() as StoreState;
    const searchParams = getSearchRequestFromUrl();
    const searchString: string = searchParams?.searchQuery || searchParam(state)!;
    store.dispatch(clearSuggestions());
    const result = await store.dispatch(
      performSearch({
        fieldValueFilter: {},
        searchTerms: searchString,
        start: 1,
        sortBy: '',
      })
    );
    const totalResults = result ? result.total : 0;
    emit({
      element: this,
      name: 'search-results',
      detail: {
        id: this.id,
        term: searchString,
        redirect: false,
        isSearchRedirect: false,
        store: undefined,
        url: window.location.href,
        user: getUserDataForUserAnalytics(state),
        totalResults,
        searchPageType: 'Search Result Page',
        catalog: selectSelectedCatalog(state),
        searchText: getSearchPageAnalytics(searchString, totalResults),
      },
    });
    //store.dispatch(performSearch({ start: 1, fieldValueFilter: {} }));
  }

  /**
   * Handle keyboard interactions for accessibility
   * Supports arrow keys, Home/End for navigation for Accessibility
   */
  private handleKeyDown(event: KeyboardEvent, tab: string) {
    const key = event.key;
    // Handle Enter and Space to activate tab
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.selectTab(tab);
      return;
    }

    // Only allow below navigation keys
    if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return;
    }

    //Avoid scroll using keywords
    event.preventDefault();
    // Get tab elements from DOM
    const tabList = (event.target as HTMLElement).closest('ul[role="tablist"]');
    const tabElements = tabList?.querySelectorAll<HTMLElement>('[role="tab"]');

    if (!tabElements || tabElements.length === 0) {
      return;
    }

    const currentIndex = Array.from(tabElements).findIndex((el) => el === event.target);
    if (currentIndex === -1) {
      return;
    }
    // Calculate target index based on key
    let targetIndex: number;
    const lastIndex = tabElements.length - 1;

    switch (key) {
      case 'ArrowRight':
      case 'ArrowDown':
        targetIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
        break;

      case 'ArrowLeft':
      case 'ArrowUp':
        targetIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
        break;

      case 'Home':
        targetIndex = 0;
        break;

      case 'End':
        targetIndex = lastIndex;
        break;

      default:
        return;
    }

    const targetTab = tabElements[targetIndex];
    if (targetTab) {
      targetTab.focus();
      // Auto-scroll the focused tab into view
      targetTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  // Render functions - unchanged
  renderComp() {
    return html`
      <div class="Default">
        <div id="horizontal-tabs" class="bs5Tabs horizontal-options-tabs horizontal-tabs tab-style-secondary-separator">
          <div class="tabs-container" data-tabbed-search="tabbed-search">
            <!-- Nav tabs with scroll event listener -->
            <!-- Left scroll button -->
            ${this.renderScrollButton('left')}

            <ul
              id="tabs"
              class="nav nav-tabs align-items-end p-1 nav-fill"
              role="tablist"
              aria-orientation="horizontal"
              @scroll=${() => this.checkScrollability()}>
              ${this.renderTab(TAB_NAMES.PRODUCT, 'collapseOne', this.productTabIcon)}
              ${this.renderTab(TAB_NAMES.SUPPORT, 'collapseTwo', this.supportTabIcon)}
              ${this.renderTab(TAB_NAMES.ARTICLES, 'collapseThree', this.articlesTabIcon)}
            </ul>
            <!-- Right scroll button -->
            ${this.renderScrollButton('right')}
          </div>

          <!-- Tab content -->
          <div class="tab-content tab-style-secondary-separator horizontal w-100" id="tabContent">
            ${this.renderTabContent('collapseOne', TAB_NAMES.PRODUCT)}
            ${this.renderTabContent('collapseTwo', TAB_NAMES.SUPPORT, true)}
            ${this.renderTabContent('collapseThree', TAB_NAMES.ARTICLES)}
          </div>
        </div>
      </div>
    `;
  }

  private renderScrollButton(direction: 'left' | 'right') {
    // Only render buttons when needed
    if (direction === 'left' && (!this.isScrollable || !this.canScrollLeft)) {
      return html``;
    }
    if (direction === 'right' && (!this.isScrollable || !this.canScrollRight)) {
      return html``;
    }

    const isLeft = direction === 'left';
    const clickHandler = isLeft ? this.scrollToLeft.bind(this) : this.scrollToRight.bind(this);
    const ariaLabel = `Scroll tabs ${direction}`;
    const icon = this.uxtIconReady ? html`<uxt-icon icon="chevron-${direction}"></uxt-icon>` : isLeft ? '<' : '>';

    return html`
      <button
        @click=${clickHandler}
        class="scroll-button scroll-${direction}"
        aria-label="${ariaLabel}"
        aria-controls="tabs">
        ${icon}
      </button>
    `;
  }

  private renderTab(tabName: string, panelId: string, iconName: string) {
    const isActive = this.activeTabKey === tabName;
    return html`
      <li class="nav-item horizontal-tab" role="presentation">
        <button
          type="button"
          id="${panelId}-tab"
          class="nav-link p-3 text-nowrap text-decoration-none fw-bold fs-6 ${isActive ? 'active' : ''}"
          tabindex="${isActive ? '0' : '-1'}"
          role="tab"
          aria-controls="${panelId}"
          aria-selected="${isActive}"
          @click="${() => this.selectTab(tabName)}"
          @keydown="${(e: KeyboardEvent) => this.handleKeyDown(e, tabName)}">
          ${iconName ? html`<uxt-icon aria-hidden="true" icon=${iconName}></uxt-icon>&nbsp;` : ''}${this.tabValueMap[
            tabName
          ]}
        </button>
      </li>
    `;
  }

  private renderTabContent(panelId: string, tabName: string, isV2 = false) {
    const isActive = this.activeTabKey === tabName;

    return html`
      <div
        class="tab-pane fade ${isActive ? 'show active' : ''}"
        id="${panelId}"
        role="tabpanel"
        aria-labelledby="${panelId}-tab">
        <div class="pt-3" tabindex="-1">
          ${isActive && this.isLoading ? generateLoadingTemplate() : this.renderTabBody(isV2, tabName)}
        </div>
      </div>
    `;
  }

  private renderTabBody(isV2 = false, tabName: string) {
    // Render only if the tab is active
    if (this.activeTabKey !== tabName) {
      return html``;
    }

    return isV2
      ? html`
          <mhecomm-search-v2-results
            ?autoload="${this.autoLoad}"
            selectedTabName="${this.tabValueMap[tabName]}"></mhecomm-search-v2-results>
          <mhecomm-search-v2-pagination
            maxPagesShown="${this.maxPagesShown}"
            message="${this.message}"
            class="w-100"
            id="tabbed-search-pagination_message"
            data-automation-id="tabbed-search-pagination_message"></mhecomm-search-v2-pagination>
        `
      : html`
          <mhecomm-search-results
            variation="${this.variation}"
            view="${this.view}"
            selectedTabName="${tabName}"
            selectedTabValue="${this.tabValueMap[tabName]}"></mhecomm-search-results>
          <mhecomm-pagination
            maxPagesShown="${this.maxPagesShown}"
            message="${this.message}"
            class="w-100"
            id="tabbed-search-pagination_message"
            data-automation-id="tabbed-search-pagination_message"></mhecomm-pagination>
        `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tabbed-search-result': TabbedSearchResultView;
  }
}
